import type { SignInActionOutcome } from '../context/AuthContext';

// Copy for the result of adding a sign-in method (SF-167; UX review 1 in the task record). Only an
// action this app started is stated as fact; a manual-link return claims nothing.
export interface SignInActionMessage {
  severity: 'success' | 'info' | 'warning';
  text: string;
}

// It must not steer a user who just finished setup into adding the same method again (UX review 2).
export const LINK_RETURN_TEXT =
  "We can't confirm whether your sign-in settings changed. If you completed setup, you can use the new method the next time you sign in.";

export const signInActionMessage = (outcome: SignInActionOutcome): SignInActionMessage => {
  if (outcome.source === 'link') return { severity: 'info', text: LINK_RETURN_TEXT };
  switch (outcome.status) {
    case 'success':
      if (outcome.action === 'webauthn-register') {
        return { severity: 'success', text: 'Passkey added. You can now use it when signing in.' };
      }
      if (outcome.action === 'CONFIGURE_TOTP') {
        return { severity: 'success', text: 'Authenticator app added. You can now use it when signing in.' };
      }
      return { severity: 'success', text: 'Sign-in method added. You can now use it when signing in.' };
    case 'cancelled':
      return { severity: 'info', text: 'Setup cancelled.' };
    case 'error':
      return {
        severity: 'warning',
        text: "We couldn't complete setup. Try again. If the problem continues, contact your administrator.",
      };
    default:
      return { severity: 'info', text: "We couldn't confirm the result of the setup." };
  }
};

/** The verified success line kept on the sign-in failure screen, or null. */
export const verifiedSuccessText = (outcome: SignInActionOutcome | null): string | null =>
  outcome && outcome.source === 'app' && outcome.status === 'success' ? signInActionMessage(outcome).text : null;
